import type { NextPage } from 'next';
import { useSession } from 'next-auth/react';

import { Container } from '@dansk-metal/ui';

import { ErrorFrame } from '@web/components/error-frame/error-frame';
import HeroTitle from '@web/components/hero-block/hero-title/hero-title';
import { PageCard } from '@web/components/page-card/page-card';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import TemplateLayout from '@web/layouts/template/template';

import { useGetUser } from '@web/services/crm-api/hooks';
import { CRMUserType } from '@web/services/crm-api/token';
import { isUserType } from '@web/services/crm-api/user-type.guard';
import { crmError } from '@web/services/errorhandler/appError';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { UmbracoBlock } from '@web/services/umbraco/types/basic/Block';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoErrorType } from '@web/services/umbraco/types/basic/ErrorTypes';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import { MemberOverviewCardBlock } from '@web/templates/user-member-page/page-group/page-group';

import { shouldRenderPageGroup, UnionRepGroupRuleset } from './should-render-page-group';

import styles from './union-rep-services-page.module.scss';

export const unionRepServicesPageContentAlias = 'unionRepServicesPage' as const;

export type UnionRepServicesPageData = UmbracoPage<
	typeof unionRepServicesPageContentAlias,
	{
		hero: {
			title: string;
			subtitle?: string;
		};
		groups: UnionRepGroupBlock[];
	}
>;

export type UnionRepGroupBlock = UmbracoBlock<
	'unionRepServicesGroup',
	{
		title: string;
		pages: MemberOverviewCardBlock[];
		userDataRuleset: UnionRepGroupRuleset;
	}
>;

const UnionRepServicesPageTemplate: NextPage<{ pageData: UnionRepServicesPageData }> = ({ pageData }) => {
	const { hero, groups } = pageData.properties;
	const { data: member, error, isLoaded } = useGetUser();
	const { data: session } = useSession();

	if (error) {
		throw crmError({ cause: error, message: 'Failed to fetch user' });
	}

	if (
		member && !(member?.flags?.isTillidsRepresentant || member?.flags?.isArbejdsmarkedRepresentant)
		&& session && !isUserType(session, CRMUserType.EmployedAtDM)) {
		return <ErrorFrame errorType={UmbracoErrorType.Forbidden} />;
	}

	return (
		(<TemplateLayout>
			{!!hero.title && (
				<HeroTitle
					title={hero.title}
					subtitle={hero.subtitle}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}
			{!isLoaded
				? (
					<Container className={styles.container}>
						<h3 className="h4">
							<ThemedSkeleton className={styles.skeleton_title} />
						</h3>
						<ThemedSkeleton
							containerClassName={styles.pages}
							className={styles.skeleton_theme}
							count={3}
							inline={true}
						/>
					</Container>
				)
				: groups
					.filter((group) => {
						if (shouldRenderPageGroup(group.contentProperties.userDataRuleset, member)) {
							return true;
						}

						return session && isUserType(session, CRMUserType.EmployedAtDM);
					})
					.map((group) => {
						const { title, pages } = group.contentProperties;

						return (
							<Container key={group.key} className={styles.container}>
								<h3 className="h4">{title}</h3>
								<div className={styles.pages}>
									{pages.map((page) => (
										<PageCard
											key={page.key}
											title={page.contentProperties.title}
											link={page.contentProperties.link}
											contentClassName={styles.content}
										>
											<p>{page.contentProperties.bodyText}</p>
										</PageCard>
									))}
								</div>
							</Container>
						);
					})
			}
		</TemplateLayout>)
	);
};

export default UnionRepServicesPageTemplate;
